<template>
  <Teleport to="body" :disabled="!appendToBody">
    <div
      v-if="isItemShowing"
      :class="appendToBody && 'lego-dropdown-menu__container'"
      :style="calcPosition"
    >
      <div ref="dropdownMenu" :class="menuClass" :style="{ ...itemsWidth, ...menuStyle }">
        <div v-if="autoComplete" class="auto-complete__search-box">
          <lego-text-field
            :modelValue="autoCompleteText"
            :placeholder="autoCompletePlaceholder"
            @update:modelValue="changeAutoCompleteText"
            :counter="counter"
            class="lego-dropdown__autocomplete"
            ref="autoCompleteInput"
            clearable
            :searchable="searchable"
            @click.stop="focused = true"
          />
        </div>
        <template v-if="autoComplete && autoCompleteText.length > 0">
          <c-scroll :style="itemsStyle">
            <div class="lego-dropdown__items" :class="itemsClass" ref="items">
              <div class="items-title">
                {{ t('label.searchResult') }} ({{ filteredItemList.length }})
              </div>
              <template v-if="filteredItemList.length > 0">
                <div
                  class="lego-dropdown__item"
                  :class="{
                    'lego-dropdown__item--focused': idx === focusedItemIndex,
                    'lego-dropdown__disabled': item.disabled,
                    'lego-dropdown__selected': isSelectedItem(item)
                  }"
                  v-for="(item, idx) in filteredItemList"
                  @click.stop="clickItem(item)"
                  :key="idx"
                  ref="legoDropdownItem"
                >
                  <div
                    class="lego-dropdown__label"
                    :class="{
                      'lego-dropdown__selected': isSelectedItem(item)
                    }"
                  >
                    <span v-lego-tooltip:ellipsis="getText(item)" class="ellipsis">
                      <slot name="itemText" :item="item" :getText="getText">
                        <span v-html="autoCompleteStyle(getText(item))" />
                      </slot>
                    </span>
                    <lego-icon-wrapper
                      v-if="item.childItems"
                      style="width: 16px; margin-left: 12px"
                    >
                      <lego-icon
                        class="lego-dropdown__label-caret"
                        icon-type="df"
                        icon="arrow_down"
                        :size:="16"
                      />
                    </lego-icon-wrapper>
                  </div>

                  <div
                    class="lego-dropdown__child-items"
                    v-if="item.childItems"
                    :style="itemsWidth"
                  >
                    <div
                      class="lego-dropdown__item lego-dropdown__child-item"
                      :class="{
                        'lego-dropdown__item--focused':
                          childIndex === focusedChildItemIndex && focusedDepthIndex === 1,
                        'lego-dropdown__disabled': item.disabled
                      }"
                      v-for="(child, childIndex) in item.childItems"
                      :key="idx + '.' + childIndex"
                      ref="legoDropdownChildItem"
                      @click.stop="clickItem(child)"
                    >
                      <div
                        class="lego-dropdown__label"
                        :class="{
                          'lego-dropdown__selected': isSelectedItem(child)
                        }"
                      >
                        <span v-lego-tooltip:ellipsis="getText(child)" class="ellipsis">
                          <slot name="itemText" :item="child" :getText="getText">
                            {{ getText(child) }}
                          </slot>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="no-result">{{ t('label.noSearchResult') }}</div>
              </template>
            </div>
          </c-scroll>
        </template>
        <template v-if="!autoCompleteText && items.length > 0">
          <div class="lego-dropdown__items" :class="itemsClass">
            <div
              class="lego-dropdown__item lego-dropdown__selectall"
              v-show="selectAll"
              @click="clickSelectAll"
            >
              <lego-checkbox
                :indeterminate="selectAllChecked && selectAllCheckedIcon === 'indeterminate'"
                :checked="selectAllChecked"
                :checked-icon="selectAllCheckedIcon"
                @click="clickSelectAll"
                >{{ selectAllText || t('label.selectAll') }}
              </lego-checkbox>
            </div>
            <c-scroll :style="itemsStyle">
              <div
                :class="groups.length > 0 && 'lego-dropdown__group'"
                v-for="(group, index) in groupList"
                :key="index"
              >
                <div v-if="group.name" class="group-name">
                  {{ group.name }} ({{ itemsList(group).length }})
                </div>
                <template>
                  <slot name="menuHeader"></slot>
                </template>
                <template v-if="itemsList(group).length > 0">
                  <div
                    class="lego-dropdown__item"
                    :class="{
                      'lego-dropdown__item--focused': idx === focusedItemIndex,
                      'lego-dropdown__disabled': item.disabled,
                      'lego-dropdown__selected': isSelectedItem(item),
                      hasScroll: itemsList(group).length > hasScroll
                    }"
                    v-for="(item, idx) in itemsList(group)"
                    @click.stop="clickItem(item)"
                    v-show="!item.hide"
                    :key="idx"
                    ref="legoDropdownItem"
                  >
                    <div v-if="!item.customButton" class="lego-dropdown__label">
                      <lego-checkbox
                        v-if="multiSelect"
                        :value="item"
                        :checked="selectedItem"
                        @click="clickItem(item)"
                      >
                        <c-label
                          v-if="isTooltip"
                          ellipsis
                          style="width: 100%; overflow: hidden !important"
                          :text="getText(item)"
                        >
                        </c-label>
                        <span v-else>{{ getText(item) }}</span>
                      </lego-checkbox>

                      <span v-else v-lego-tooltip:ellipsis="getText(item)" class="ellipsis">
                        <slot name="itemText" :item="item" :getText="getText">
                          {{ getText(item) }}
                        </slot>
                      </span>
                      <lego-icon-wrapper
                        v-if="item.childItems"
                        style="width: 16px; margin-left: 12px"
                      >
                        <lego-icon
                          class="lego-dropdown__label-caret"
                          icon-type="df"
                          icon="arrow_down"
                          :size:="16"
                        />
                      </lego-icon-wrapper>
                    </div>
                    <div
                      class="lego-dropdown__child-items"
                      v-if="item.childItems"
                      :style="itemsWidth"
                    >
                      <div
                        class="lego-dropdown__item lego-dropdown__child-item"
                        :class="{
                          'lego-dropdown__item--focused':
                            childIndex === focusedChildItemIndex && focusedDepthIndex === 1,
                          'lego-dropdown__disabled': item.disabled
                        }"
                        v-for="(child, childIndex) in item.childItems"
                        :key="idx + '.' + childIndex"
                        ref="legoDropdownChildItem"
                        @click.stop="clickItem(child)"
                      >
                        <div
                          class="lego-dropdown__label"
                          :class="{
                            'lego-dropdown__selected': isSelectedItem(child)
                          }"
                        >
                          <span v-lego-tooltip:ellipsis="getText(child)" class="ellipsis">
                            <slot name="itemText" :item="child" :getText="getText">
                              {{ getText(child) }}
                            </slot>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="no-data">
                    <div>{{ handleNoDataText }}</div>
                  </div>
                </template>
              </div>
            </c-scroll>
          </div>
        </template>
        <div v-if="items.length === 0" class="no-data">
          <div>{{ handleNoDataText }}</div>
        </div>
        <div class="lego-dropdown__apply lego-dropdown__apply_pos" v-if="multiSelect && applyBtn">
          <lego-button @click="onApplyClick" sm main>{{ t('button.apply') }}</lego-button>
        </div>
        <div
          v-if="customButtonText"
          class="lego-dropdown-menu__custom_button"
          @click="customButtonClick"
        >
          <lego-icon iconType="df" icon="add_border" />
          {{ customButtonText }}
        </div>
      </div>
    </div>
  </Teleport>
</template>
<script>
import i18nMixin from '@/mixins/i18n.mixin.js';

const DEFAULT_ITEM_TEXT = 'text';
const DEFAULT_ITEM_VALUE = 'value';

export default {
  mixins: [i18nMixin],
  emits: ['click', 'selectAll', 'apply', 'close', 'customButtonClick', 'changeAutoCompleteText'],
  props: {
    items: {
      type: [Array, Object]
    },
    isItemShowing: {
      type: Boolean,
      default: false
    },
    autoComplete: {
      type: Boolean,
      default: false
    },
    counter: {
      type: [String, Number],
      default: undefined
    },
    autoCompleteText: {
      type: String,
      default: ''
    },
    autoCompletePlaceholder: {
      type: String,
      default: ''
    },
    multiSelect: {
      type: Boolean,
      default: false
    },
    selectAllText: { type: String, default: '' },
    selectAll: {
      type: Boolean,
      default: false
    },
    selectAllChecked: {
      type: Boolean,
      default: false
    },
    selectAllCheckedIcon: {
      type: String,
      default: 'checked'
    },
    applyBtn: {
      type: Boolean,
      default: false
    },
    width: { type: [Number, String] /*px*/ },
    itemMaxHeight: { type: [Number, String], height: 272 },
    filteredItemList: {
      type: Array
    },
    itemText: {
      type: [String, Function],
      default: DEFAULT_ITEM_TEXT
    },
    itemValue: { type: String, default: DEFAULT_ITEM_VALUE },
    selectedItem: {
      type: [Object, String, Array]
    },
    focusedItemIndex: { type: Number },
    focusedDepthIndex: { type: Number },
    appendToBody: { type: Boolean, default: false },
    targetInterval: { type: Number, default: 0 },
    menuDirection: {
      type: String,
      default: 'left',
      validator: function (value) {
        return ['left', 'right', 'center'].indexOf(value) !== -1;
      }
    },
    shadow: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    customButtonText: {
      type: String,
      default: ''
    },
    noDataText: {
      type: String,
      default: ''
    },
    parent: {
      type: String,
      default: ''
    },
    groups: {
      type: Array,
      default: () => []
    },
    groupSort: {
      type: Boolean,
      default: false
    },
    isTooltip: {
      type: Boolean,
      default: false
    },
    autoSearch: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    capitalize: {
      type: Boolean,
      default: false
    },
    hasScroll: {
      type: Number
    }
  },
  data: () => ({
    target: {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    },
    el: null,
    elWidth: 0,
    elHeight: 0,
    focused: false
  }),
  watch: {
    isItemShowing(val) {
      if (val) {
        if (this.appendToBody) {
          this.$nextTick(() => {
            this.el = this.$refs.dropdownMenu;
            if (this.el) {
              this.elWidth = this.el.clientWidth;
              const elHeight = this.el.clientHeight;
              if (elHeight) {
                this.elHeight = elHeight;
              }
            }

            this.getTargetInfo();
            window.addEventListener('resize', this.getTargetInfo);
          });
        } else {
          this.$nextTick(() => {
            if (this.$refs.dropdownMenu) {
              this.elWidth = this.$refs.dropdownMenu.clientWidth;
            }
          });
        }

        if (this.autoComplete) {
          this.$nextTick(() => {
            this.$refs.autoCompleteInput.focus();
          });
        }
      } else {
        if (this.el) {
          window.removeEventListener('resize', this.getTargetInfo);
        }
      }
    }
  },
  mounted() {
    this.getTargetInfo();
  },
  computed: {
    handleNoDataText() {
      return this.noDataText || this.$t('label.noData');
    },
    menuClass() {
      let clazz = ['lego-dropdown-menu'];

      if (this.small) clazz.push('lego-dropdown-menu__small');
      if (this.shadow) clazz.push('lego-dropdown-menu__shadow');
      if (!this.appendToBody && this.menuDirection === 'right')
        clazz.push('lego-dropdown-menu--right');
      if (!this.appendToBody && this.menuDirection === 'center')
        clazz.push('lego-dropdown-menu--center');

      return clazz;
    },
    hasChildItems() {
      let flag = false;
      this.items.forEach((item) => {
        flag = flag || !!item.childItems;
      });
      return flag;
    },
    itemsClass() {
      let clazz = [];
      if (this.hasChildItems) clazz.push('lego-dropdown__items--has-child-items');
      return clazz;
    },
    itemsWidth() {
      let widthStyle = {};
      if (this.width) {
        widthStyle.width = this.getNumber(this.width) + 'px';
        if (typeof this.width === 'string' && this.width.includes('%')) {
          widthStyle.width = this.width;
        }
      } else if (this.parent === 'legoDropHolder') {
        widthStyle.width = 'auto';
      } else {
        widthStyle.width = this.getNumber(this.target.width) + 'px';
      }
      return widthStyle;
    },
    menuStyle() {
      if (!this.appendToBody && this.menuDirection === 'center' && this.elWidth) {
        return {
          marginTop: this.targetInterval - 1 + 'px',
          marginLeft: `-${this.elWidth / 2}px`
        };
      }
      return { marginTop: this.targetInterval + 'px' };
    },
    itemsStyle() {
      return { maxHeight: this.getNumber(this.itemMaxHeight) + 'px' };
    },
    calcPosition() {
      if (!this.appendToBody) {
        return null;
      }

      let menuPosition = {
        x: 0,
        y: 0
      };

      if (this.menuDirection === 'left') {
        menuPosition.x = this.target.x;
      } else if (this.menuDirection === 'right') {
        menuPosition.x = this.target.x + this.target.width - this.elWidth;
      } else if (this.menuDirection === 'center') {
        menuPosition.x = this.target.x + this.target.width / 2 - this.elWidth / 2;
      }
      menuPosition.y = this.target.y + this.target.height + this.targetInterval;
      if (document.body.clientHeight - menuPosition.y < this.elHeight) {
        menuPosition.y = this.target.y - this.targetInterval - this.elHeight;
      }
      return { left: `${menuPosition.x}px`, top: `${menuPosition.y}px` };
    },
    groupList() {
      if (this.groups.length === 0) {
        return { all: true };
      }
      return this.groups;
    }
  },
  methods: {
    autoCompleteStyle(val) {
      let valClone = val;
      let searchText = this.autoCompleteText.trim();

      if (!searchText) return this.$util.escapeHTML(val);
      if (this.autoSearch) {
        const index = valClone.toLowerCase().indexOf(searchText.toLowerCase());
        const endIndex = index + searchText.length;
        const searchValue = this.$util.escapeHTML(val.slice(index, endIndex));
        return this.$util
          .escapeHTML(val)
          .replace(searchValue, `<span class="point bold">${searchValue.trim()}</span>`);
      }
      const autoCompleteText = this.$util.escapeHTML(this.autoCompleteText.trim());
      const regex = autoCompleteText.replace(/[!@#$%^&*()+=\-[\]\\';,./{}|":<>?~_]/g, '\\$&');
      return this.$util
        .escapeHTML(val)
        .replace(new RegExp(regex, 'ig'), (w) => `<span class="point">${w}</span>`);
    },
    itemSort(groupItems) {
      groupItems.sort((a, b) => {
        return a.groupIdx - b.groupIdx;
      });
    },
    itemsList(group) {
      if (group.all) {
        return this.items;
      }

      if (!group.group) {
        return this.items.filter((item) => !item.group);
      }

      if (this.groupSort) {
        const groupCopy = JSON.parse(JSON.stringify(this.items));
        const groupItems = groupCopy.filter((item) => item.group === group.group);
        this.itemSort(groupItems);
        return groupItems;
      }

      return this.items.filter((item) => item.group === group.group);
    },
    getTargetInfo() {
      this.$nextTick(() => {
        if (this.$parent.$refs[this.parent]) {
          const target = this.$parent.$refs[this.parent].getBoundingClientRect();

          this.target.x = target.left + window.pageXOffset;
          this.target.y = target.top + window.pageYOffset;
          this.target.width = target.width;
          this.target.height = target.height;
        }
      });
    },
    getNumber(str) {
      let v = str;
      if (str && isNaN(v)) {
        v = v.replace(/[^0-9]/g, '');
      }
      return parseInt(v);
    },
    getValue(item) {
      if (!item) return null;
      let itemValue = item[this.itemValue];
      if (itemValue === undefined || itemValue === null) {
        return item;
      } else {
        return itemValue;
      }
    },
    getText(item) {
      if (!item) return this.autoComplete ? '' : this.placeholder || this.default.placeholder;
      let itemText = '';
      if (typeof this.itemText === 'function') {
        itemText = this.itemText(item);
      } else {
        itemText = item[this.itemText];
      }
      if (itemText === undefined || itemText === null) {
        return item;
      } else {
        if (this.capitalize) {
          itemText = this.$util.capitalize(itemText);
        }

        return itemText;
      }
    },
    clickItem(item) {
      this.$emit('click', item);
    },
    isSelectedItem(item) {
      if (this.multiSelect) {
        return this.selectedItem.includes(item);
      }
      return this.selectedItem === item;
    },
    clickSelectAll() {
      this.$emit('selectAll');
    },
    onApplyClick() {
      this.$emit('apply');
    },
    closeMenu() {
      if (this.isItemShowing) {
        this.$emit('close');
      }
    },
    customButtonClick() {
      this.$emit('customButtonClick');
    },
    changeAutoCompleteText(val) {
      this.$emit('changeAutoCompleteText', val);
    }
  }
};
</script>
<style lang="scss" scoped>
.lego-dropdown-menu {
  position: absolute;
  border: 1px solid $lego-dropdown__items__border-color;
  min-width: 100%;
  margin-top: 0px;
  background-color: $color--ui-11;
  z-index: 200;
  cursor: default;

  .lego-dropdown__group {
    margin-top: 5px;

    & + .lego-dropdown__group {
      padding-top: 5px;
      border-top: 1px solid $color--ui-09;
    }
  }

  .group-name,
  .items-title {
    font-size: 12px;
    color: $color--ui-05;
    padding: 0 10px;
  }

  .lego-dropdown__items {
    position: relative;
    width: auto;
    border: 0;
    min-width: 100%;
    max-height: 100%;
    margin-top: 0;
    overflow: visible;

    .lego-dropdown__item {
      :deep .point {
        color: $color--primary-tint06;

        &.bold {
          font-weight: 700;
        }
      }

      .lego-dropdown__label {
        cursor: pointer;
        min-height: 34px;
        @include font($size: 14px);
      }

      .lego-checkbox {
        width: 100%;
        line-height: 34px;

        :deep .lego-checkbox__label {
          @include ellipsis;
          flex: 0 1 auto;
        }

        :deep .lego-checkbox__icon {
          flex: 0 0 16px;
        }
      }

      :deep .lego-checkbox__checked {
        & + .lego-checkbox__label {
          font-weight: normal;
        }
      }

      &:active,
      &.lego-dropdown__selected {
        background-color: $lego-dropdown__item__background-color--active;
        color: $lego-dropdown__item__font-color--selected;
      }

      &.lego-dropdown__disabled {
        pointer-events: none;
        cursor: default;

        .lego-dropdown__label {
          opacity: 0.4;
        }
      }
    }

    :deep .simplebar-offset {
      width: 100%;
    }
  }

  .lego-dropdown__selectall {
    position: relative;
    border: 0;
    padding: 0 10px;
    margin: 0;

    &:after {
      width: calc(100% - 20px);
      position: absolute;
      bottom: 0px;
      content: '';
      border-bottom: 1px solid $color--ui08;
    }
  }

  &__shadow {
    box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.5);
  }

  &__custom_button {
    border-top: 1px solid $color--ui-09;
    color: $color--primary;
    margin-bottom: 6px;
    padding: 0 10px;
    cursor: pointer;
    height: 32px;
    line-height: 32px;
  }

  &--right {
    right: 0;
  }

  &--center {
    left: 50%;
  }

  .lego-dropdown__apply {
    border: 0;
    border-top: 1px solid $color--ui08;
    margin: 0 10px;
  }

  .no-data,
  .no-result {
    cursor: default;
    padding: 25px 0;
    text-align: center;
    font-size: 11px;
    color: $color--ui-04;
  }

  .auto-complete__search-box {
    padding: 8px;
  }
}

.lego-dropdown-menu__custom_button {
  .lego-lcon {
    margin-right: 8px;
  }

  &:hover,
  &:active {
    background-color: rgba($color--primary, 0.12);
    color: $color--primary;
  }
}
</style>
<style lang="scss">
.auto-complete__search-box .lego-text-field__input {
  border: solid 1px $color--ui-06 !important;
}

.auto-complete__search-box .lego-text-field__input:focus {
  border: solid 1px $color--primary !important;
}

.lego-dropdown__item:first-of-type {
  border-radius: 4px 4px 0 0;
}
.lego-dropdown__item:last-child {
  border-radius: 0 0 4px 4px;
}

.lego-dropdown-menu {
  border-radius: 4px;
  overflow: hidden;

  .lego-dropdown__items {
    border-color: #2c59d9;
    border-radius: 4px;
  }
}

.lego-dropdown-menu__container {
  position: absolute;

  .lego-dropdown__items--right {
    right: inherit;
  }
}
</style>
